import React from 'react';
import './AdminDashCoachingMeetings.styl';
import connectToStores from 'alt-utils/lib/connectToStores';
import AdminDashCoachingMeetings from 'components/dashboard/admin/admin-coaching-meetings/AdminDashCoachingMeetings';
import CoachingReviewTypeStore from 'stores/coaching/CoachingReviewTypeStore';
import DashV2MainWidgetLoader from 'components/dashboard/DashV2MainWidgetLoader';
import ExecDashboardSource from 'sources/ExecDashboardSource';
import {List, Map} from 'immutable';
import {DashChild} from '@store/@types/dash/DashChild';
import User from 'records/User';
import UserSource from 'sources/UserSource';

interface Props extends DashChild, ReturnType<typeof getPropsFromStores> {}

function getPropsFromStores() {
  const {meetingCategories} = CoachingReviewTypeStore.getState();

  return {
    meetingCategories,
  };
}

interface State {
  readonly meetingCategoryTimeInSeconds?: List<any>;
  readonly meetingCategoryCount?: List<any>;
  readonly meetingCount?: Map<string, number>;
  readonly planCount?: Map<string, number>;
  readonly improvement?: Map<string, number>;
  readonly teamIds?: List<string>;
  readonly users?: List<User>;
  readonly coaches?: List<User>;
  readonly loaded: boolean;
}

@connectToStores
export default class AdminDashCoachingMeetingsContainer extends React.Component<Props, State> {
  state = {loaded: false};

  static getStores() {
    return [CoachingReviewTypeStore];
  }

  static getPropsFromStores() {
    return getPropsFromStores();
  }

  loadData() {
    this.setState({loaded: false}, async () => {
      const {startDate, endDate} = this.props;

      const [users, teams] = await Promise.all([
        UserSource.fetchAll({roles: ['admin', 'manager', 'user']}),
        ExecDashboardSource.getTeamsInAccount(),
      ]);

      const tids = teams.keySeq().toJS();

      const coaches = users.filter(u => u.isCoach && tids.includes(u.teamId));

      const [
        meetingCategoryTimeInSeconds,
        meetingCategoryCount,
        meetingCount,
        planCount,
        improvement,
      ] = await Promise.all([
        ExecDashboardSource.getTeamMetric({
          teams: tids,
          subject: 'coach',
          direction: 'coach',
          startDate,
          endDate,
          metric: 'meeting_category_time',
        }),
        ExecDashboardSource.getTeamMetric({
          teams: tids,
          subject: 'coach',
          direction: 'coach',
          startDate,
          endDate,
          metric: 'meeting_category_count',
        }),
        ExecDashboardSource.getTeamMetric({
          teams: tids,
          subject: 'coach',
          direction: 'coach',
          startDate,
          endDate,
          metric: 'meetings',
        }),
        ExecDashboardSource.getTeamMetric({
          teams: tids,
          subject: 'coach',
          direction: 'coach',
          startDate,
          endDate,
          metric: 'plans',
        }),
        ExecDashboardSource.getTeamMetric({
          teams: tids,
          subject: 'coach',
          direction: 'coach',
          startDate,
          endDate,
          metric: 'coaching_plan_improvement',
        }),
      ]);

      try {
        this.setState({
          meetingCategoryTimeInSeconds: List(meetingCategoryTimeInSeconds),
          meetingCategoryCount: List(meetingCategoryCount),
          meetingCount: Map(meetingCount.map(mc => [mc.id, mc.value])),
          planCount: Map(planCount.map(pc => [pc.id, pc.value])),
          improvement: Map(improvement.map(i => [i.id, i.value])),
          teamIds: tids,
          users,
          coaches,
          loaded: true,
        });
      } catch (e) {
        // Since this promise returned by this function isn't awaited anywhere this component
        // can unmount before we get this point. Meaning this.setState() will error out.
        // As far as i can tell this only happens when reloading the app on this page. Navigating
        // here from somewhere else, wont cause it
        console.error(e);
      }
    });
  }

  componentDidMount() {
    this.loadData();
  }

  componentDidUpdate(prevProps) {
    const {startDate: prevStartDate, endDate: prevEndDate} = prevProps;
    const {startDate, endDate} = this.props;
    if (startDate !== prevStartDate || endDate !== prevEndDate) {
      this.loadData();
    }
  }

  render() {
    const {loaded} = this.state;

    if (!loaded) return <DashV2MainWidgetLoader />;

    return <AdminDashCoachingMeetings {...this.props} {...this.state} />;
  }
}
